import { FC, useCallback, useEffect, useState } from 'react';
import { SearchIndex } from 'algoliasearch';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { SearchAuthProviderProps, SearchConfig } from 'types';
import { PartTypesAuthContext } from './AuthContext';
import { getPartTypesAuth } from 'api/partTypes';
import { createNullCache } from '@algolia/cache-common';

const PART_TYPES_CONFIG_KEY = 'part_types_config';
const PART_TYPES_CONFIG_VERSION_KEY = 'part_types_config_version';
const PART_TYPES_CONFIG_VERSION = 'part_type_algolia_auth_v2';

const createPartTypesSearchClient = (appId: string, apiKey: string) =>
  algoliasearch(appId, apiKey, {
    responsesCache: createNullCache(),
    requestsCache: createNullCache(),
    hostsCache: createNullCache(),
  });

export const PartTypesAuthProvider: FC<SearchAuthProviderProps> = ({ children }) => {
  const [client, setClient] = useState<SearchClient | null>(null);
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const fetchAuthFromAPI = useCallback(async (): Promise<SearchConfig | null> => {
    try {
      const data = await getPartTypesAuth();
      const config: SearchConfig = {
        api_key: data.api_key,
        app_id: data.app_id,
        index: data.index,
        expiry_time: data.expiry_time,
      };
      localStorage.setItem(PART_TYPES_CONFIG_KEY, JSON.stringify(config));
      localStorage.setItem(PART_TYPES_CONFIG_VERSION_KEY, PART_TYPES_CONFIG_VERSION);
      const clientData = createPartTypesSearchClient(config.app_id, config.api_key);
      const indexData = clientData.initIndex(config.index);
      setClient(clientData);
      setIndex(indexData as SearchIndex);
      return config;
    } catch (error) {
      console.error('Error fetching auth token:', error);
      return null;
    }
  }, []);

  const isWithinOneMinute = (epochTime: number): boolean => {
    const currentTime = Math.round(Date.now() / 1000);

    return epochTime - currentTime < 6000;
  };

  const loadAuthFromLocalStorage = useCallback((): SearchConfig | null => {
    const configVersion = localStorage.getItem(PART_TYPES_CONFIG_VERSION_KEY);
    if (configVersion !== PART_TYPES_CONFIG_VERSION) {
      localStorage.removeItem(PART_TYPES_CONFIG_KEY);
      localStorage.setItem(PART_TYPES_CONFIG_VERSION_KEY, PART_TYPES_CONFIG_VERSION);
      return null;
    }

    const storedConfig = localStorage.getItem(PART_TYPES_CONFIG_KEY);
    if (storedConfig) {
      const config: SearchConfig = JSON.parse(storedConfig);
      if (!isWithinOneMinute(config.expiry_time)) {
        const clientData = createPartTypesSearchClient(config.app_id, config.api_key);
        const indexData = clientData.initIndex(config.index);
        setClient(clientData);
        setIndex(indexData as SearchIndex);
        return config;
      }
    }
    return null;
  }, []);

  const setApiToken = useCallback(async () => {
    let config = loadAuthFromLocalStorage();
    if (!config) {
      config = await fetchAuthFromAPI();
    }
  }, [fetchAuthFromAPI, loadAuthFromLocalStorage]);

  useEffect(() => {
    setApiToken();
    const intervalId = setInterval(
      () => {
        setApiToken();
      },
      60 * 60 * 1000,
    );
    return () => clearInterval(intervalId);
  }, [setApiToken, refetchCount]);

  return <PartTypesAuthContext.Provider value={{ client, index, setRefetchCount }}>{children}</PartTypesAuthContext.Provider>;
};

export default PartTypesAuthProvider;
